import React, { memo, useMemo } from 'react';
import { Col, Card, CardBody, Media, UncontrolledTooltip, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import { HelpArticleCategoryResponse, HelpArticleResponse, HelpArticleStatus } from '@business/entities/helpArticle';
import { Formatter } from '@core/utils/formatter';
import { HTMLService, CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { TableActions } from '@modules/common/TableActions';
import { openAddEditHelpArticleCategoryModal } from '../AddEditHelpArticleCategoryModal';
import { openAddEditHelpArticleModal } from '../AddEditHelpArticleModal';

export interface HelpArticleCardProps {
  type: 'category' | 'article';
  item: HelpArticleCategoryResponse | HelpArticleResponse;
  onDelete(): void;
  onView(type: string, itemSlug: string, categorySlug: string | undefined): void;
  isViewEnabled: boolean;
}

export const HelpArticleCard = memo((props: HelpArticleCardProps) => {
  const { type, item, onDelete, onView, isViewEnabled } = props;

  const data = useMemo(() => {
    if (type === 'category') {
      const inItem = item as HelpArticleCategoryResponse;
      return {
        title: inItem.name,
        description: inItem.description ? HTMLService.toReact(HTMLService.strip(inItem.description)) : '',
        slug: inItem.slug,
        categorySlug: inItem.slug,
        created: inItem.created,
        badge: HelpArticleStatus.private,
        link: `/help-articles/${inItem.id}`,
        createdBy: CommonService.getFullName(inItem.createdBy),
        onClick: () => openAddEditHelpArticleCategoryModal({ category: inItem }),
        showIcon: isViewEnabled,
      };
    } else {
      const inItem = item as HelpArticleResponse;
      return {
        title: inItem.title,
        description: inItem.content ? HTMLService.toReact(HTMLService.strip(inItem.content)) : '',
        slug: inItem.slug,
        created: inItem.created,
        badge: inItem.status,
        categorySlug: inItem.categorySlug,
        link: `/help-article/${inItem.id}`,
        createdBy: CommonService.getFullName(inItem.createdBy),
        onClick: () => openAddEditHelpArticleModal({ helpArticle: inItem }),
        showIcon: isViewEnabled && inItem.status === HelpArticleStatus.public,
      };
    }
  }, [type, item, isViewEnabled]);

  return (
    <Col xl="4" sm="6">
      <Card>
        <CardBody style={{ height: 100 }}>
          <Media>
            <div className="mr-4">
              <div className="font-size-16 p-4">
                {type === 'category' && <i style={{ fontSize: 30 }} className="bx bx-folder text-primary"></i>}
                {type === 'article' && <i style={{ fontSize: 30 }} className="bx bx-file text-dark"></i>}
              </div>
            </div>

            <Media className="overflow-hidden" body style={{ marginTop: 10 }}>
              <h5 className="text-truncate font-size-15">
                <Link to={data.link} className="text-dark">
                  {data.title}
                </Link>
              </h5>
              <div className="text-mute text-truncate">{data.description}</div>
            </Media>
          </Media>
        </CardBody>
        <div className="px-4 py-3 border-top">
          <ul className="list-inline mb-0">
            <li className="list-inline-item mr-3">
              {type === 'article' && <Badge color={data.badge === HelpArticleStatus.public ? 'success' : 'dark'}>{R.upperFirst(data.badge)}</Badge>}
            </li>
            <li className="list-inline-item mr-3" id={`createdAt-${type}-${item.id}`}>
              <i className="bx bx-calendar mr-1" /> {Formatter.formatTime(data.created)}
              <UncontrolledTooltip placement="top" target={`createdAt-${type}-${item.id}`}>
                {data.createdBy}
              </UncontrolledTooltip>
            </li>
            <li className="list-inline-item float-right">
              {data.showIcon ? (
                <TableActions onDelete={onDelete} onEdit={data.onClick} onView={() => onView(type, data.slug, data.categorySlug)} />
              ) : (
                <TableActions onDelete={onDelete} onEdit={data.onClick} />
              )}
            </li>
          </ul>
        </div>
      </Card>
    </Col>
  );
});
